import {
  ENUM_LABELS,
  PARAMETER_FORMULAS,
  type ParameterValue,
  type TestParameter,
} from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { type Column, DataTable } from '#client/components/ui/DataTable';
import { DetailList } from '#client/components/ui/DetailList';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { formatDateTime, dash } from '#client/lib/format';
import { RESOURCE } from '#client/lib/routes';

const PATH = RESOURCE.testParameters;

const valueColumns: Column<ParameterValue>[] = [
  {
    key: 'normal_range',
    header: 'Normal range',
    render: (row) => (row.normal_range ? <strong>{row.normal_range}</strong> : '—'),
  },
  { key: 'unit', header: 'Unit', render: (row) => dash(row.unit) },
  { key: 'age', header: 'Age', render: (row) => dash(row.age) },
  { key: 'age_group', header: 'Age group', render: (row) => dash(row.age_group) },
  {
    key: 'gender',
    header: 'Gender',
    render: (row) => (row.gender ? ENUM_LABELS.gender[row.gender] : 'Any'),
  },
  { key: 'other', header: 'Other', render: (row) => dash(row.other) },
];

export function TestParameterDetailPage() {
  return (
    <ResourceDetailPage<TestParameter>
      path={PATH}
      noun="parameter"
      editCapability="testParameter.edit"
      deleteCapability="testParameter.delete"
      title={(parameter) => parameter.name}
      subtitle={(parameter) => `Parameter ${parameter.code}`}
      deleteLabel={(parameter) => parameter.name}
    >
      {(parameter) => (
        <>
          <Card title="Details">
            <DetailList
              items={[
                { label: 'Name', value: parameter.name },
                { label: 'Code', value: parameter.code },
                {
                  label: 'Calculated',
                  value: parameter.formula
                    ? PARAMETER_FORMULAS[parameter.formula].expression
                    : 'No — typed',
                },
                { label: 'Created', value: formatDateTime(parameter.created_at) },
                {
                  label: 'Preset results',
                  value:
                    parameter.results.length > 0
                      ? parameter.results.map((result) => result.value).join(', ')
                      : '—',
                  wide: true,
                },
              ]}
            />
          </Card>

          <Card title={`Normal ranges (${parameter.values.length})`} flush>
            <DataTable
              columns={valueColumns}
              rows={parameter.values}
              rowKey={(row) => row.id}
              caption={`Reference ranges for ${parameter.name}`}
              emptyState="No ranges defined"
            />
          </Card>
        </>
      )}
    </ResourceDetailPage>
  );
}
